// A per-bet save queue: guarantees that saving one bet never silently drops
// another, and that the user's LATEST pick is the one that reaches the server.
//
// Background (issue-analysis.md cause #1): ScoreBet used a single module-level
//   _.throttle(savePicks, 450, {leading: false, trailing: true})
// shared by EVERY ScoreBet on the page. Lodash throttle coalesces all calls in
// a 450ms window into ONE trailing call with the LAST arguments, so completing
// match A and then match B within 450ms dispatched only B — match A's request
// never left the browser and its onOk/onError callbacks never fired (silent
// data loss, no error shown). `leading: false` also delayed every single save
// by 450ms, losing it entirely if the user navigated away first.
//
// This queue replaces that with "send latest, at most one in flight" semantics,
// scoped PER bet. Create one queue per component instance (via useRef) — never
// module-level, which is exactly what caused the cross-bet drop:
//
//   * The first save is dispatched immediately (no artificial 450ms delay).
//   * While a request is in flight, further saves do NOT start a second
//     request; they overwrite a single "pending" slot that always holds the
//     LATEST picks.
//   * When the in-flight request settles, a waiting pending save (if any) is
//     dispatched next. So the server converges on the user's last pick, and no
//     save is ever coalesced away carrying a different bet's arguments.
//   * Every request that is actually dispatched reports back through its own
//     onOk/onError, so the UI can only show success for a real server response.
//
// `savePicksFn` is injected into createSaveQueue so the queue itself stays free
// of network/i18n imports and is trivially unit-testable in isolation (the
// useQueuedSavePickRef hook below is the only part that reaches for savePicks).
//
// Note: like the throttle it replaces, this does not add a request timeout, so
// a request that never settles (issue-analysis.md cause #7) blocks this bet's
// pending save. Fixing that belongs with #7 (axios timeout + bounded retry).

import { useRef } from 'react'

import savePicks from '@root/bet_utils/save_picks'

export const createSaveQueue = (savePicksFn) => {
  let inFlight = false
  let pending = null // most recent save requested while a request was in flight

  const dispatch = (job) => {
    inFlight = true
    savePicksFn(
      job.bet,
      job.authenticityToken,
      job.pickIds,
      /* OK    */ (msg, fullResp) => { try { job.onOk(msg, fullResp) } finally { settle() } },
      /* ERROR */ (errorMsg) => { try { job.onError(errorMsg) } finally { settle() } }
    )
  }

  const settle = () => {
    inFlight = false
    if (pending) {
      const next = pending
      pending = null
      dispatch(next)
    }
  }

  return (bet, authenticityToken, pickIds, onOk, onError) => {
    const job = { bet, authenticityToken, pickIds, onOk, onError }
    if (inFlight) {
      pending = job // supersede any earlier pending: only the latest picks matter
    } else {
      dispatch(job)
    }
  }
}

// React hook that gives a component its OWN per-instance save queue. It returns
// the ref OBJECT (not ref.current) on purpose: the queue function is stable for
// the component's whole lifetime, so callers can read `saveQueueRef.current(...)`
// inside a useCallback/useEffect without listing the queue as a dependency. The
// ref is lazily populated so the queue is created once and stays stable across
// renders — and, crucially, is never shared between bets (that sharing was the
// cross-bet silent-drop bug this whole change fixes).
export const useQueuedSavePickRef = () => {
  const saveQueueRef = useRef(null)
  if (saveQueueRef.current === null) {
    saveQueueRef.current = createSaveQueue(savePicks)
  }
  return saveQueueRef
}

export default createSaveQueue
